/*
 * START_MODULE_CONTRACT
 *   PURPOSE: IntersectionObserver-based reveal animation — fade/slide children into viewport
 *   PROPS:   direction (up|down|left|right), delay (ms), duration (ms), className
 *   STATE:   visible (boolean) — toggled when element enters viewport
 *   DEPENDS: M-CN (cn utility)
 *   EMITS:   none
 *   ACCESSIBILITY: respects prefers-reduced-motion via globals.css override
 *   LINKS:   M-SRL
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-12] Added GRACE MODULE_CONTRACT and semantic blocks
 *   [2026-06-12] Fixed P0 violation: replaced inline style={{}} with CSS custom properties
 */

"use client"

import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/cn"

// --- TypeDefinitions ---
interface ScrollRevealProps {
  children: React.ReactNode
  className?: string
  direction?: "up" | "down" | "left" | "right"
  delay?: number
  duration?: number
}

// --- ScrollRevealComponent ---
export function ScrollReveal({
  children,
  className,
  direction = "up",
  delay = 0,
  duration = 600,
}: ScrollRevealProps) {
  // --- IntersectionState ---
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  // --- ObserveViewport ---
  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.unobserve(el)
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // --- DirectionClasses ---
  const dirClasses = {
    up: "translate-y-8",
    down: "-translate-y-8",
    left: "translate-x-8",
    right: "-translate-x-8",
  }

  // --- Render ---
  return (
    <div
      ref={ref}
      className={cn(
        "transition-all ease-[cubic-bezier(0.4,0,0.2,1)]",
        visible ? "translate-x-0 translate-y-0 opacity-100" : cn("opacity-0", dirClasses[direction]),
        className,
      )}
      style={{
        "--reveal-duration": `${duration}ms`,
        "--reveal-delay": `${delay}ms`,
        transitionDuration: "var(--reveal-duration)",
        transitionDelay: "var(--reveal-delay)",
      } as React.CSSProperties}
    >
      {children}
    </div>
  )
}
